import { useState } from 'react';
import { Description, ExpandMore } from '@mui/icons-material';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Box
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { SelectAsync } from '../../../components';
import { getNeighborhoodUpgradePricingReport } from '../../../fetch';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import { downloadFile, getDownloadFileName } from '../../../helpers';
import { SalesNeighborhoodDropdown } from '../../../components/dropdowns/sales-neighborhood-dropdown';
import { IDropdownResponse } from '../../../models';
import { getSalesNeighborhoodAvailableLots } from '../../../fetch/lookups/sales-portal';
import { SalesPlansDropdown } from '../../../components/dropdowns/sales-plans-dropdown';

export const UpgradePricing = ({ shouldSeeAllNeighborhoods }: { shouldSeeAllNeighborhoods: boolean }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isGeneratingDoc, setIsGeneratingDoc] = useState<boolean>(false);

  const handleDownload = async (neighborhood: number, lot: number, plan: number) => {
    try {
      setIsGeneratingDoc(true);
      const res = await getNeighborhoodUpgradePricingReport(neighborhood, lot, {
        neighborhoodId: neighborhood,
        lotNumber: lot,
        planId: plan
      });
      return downloadFile(res.data, getDownloadFileName(res));
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error, unable to download file, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  return (
    <>
      <Accordion className={classes.root}>
        <AccordionSummary
          className={classes.accordion}
          expandIcon={<ExpandMore />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div className={classes.topPanelSummaryWrapper}>
            <Typography variant="h6">Upgrade Pricing</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.details}>
          <div className={classes.panelSummaryWrapper}>
            <Box mt={1}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  neighborhoods: '',
                  lot: '',
                  plans: ''
                }}
                onSubmit={() => {}}
              >
                {({
                  isSubmitting,
                  values,
                  initialValues,
                  setFieldValue,
                  handleSubmit,
                  dirty,
                  isValid,
                  handleBlur,
                  errors,
                  touched
                }) => {
                  return (
                    <>
                      <Form onSubmit={() => handleSubmit} autoComplete="none">
                        <Grid container spacing={1} flexWrap={{ xs: 'wrap', xl: 'nowrap' }}>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesNeighborhoodDropdown
                              name={'neighborhoods'}
                              isRequired={true}
                              value={values.neighborhoods}
                              isDisabled={isGeneratingDoc}
                              handleChange={val => {
                                setFieldValue('neighborhoods', val);
                                setFieldValue('lot', '');
                                setFieldValue('plans', '');
                              }}
                              apiParams={{
                                isActive: true,
                                hasAvailableLots: true,
                                allNeighborhoods: shouldSeeAllNeighborhoods
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SelectAsync
                              required
                              label="Lots"
                              name="Lot"
                              value={values.lot}
                              disabled={!values.neighborhoods || isGeneratingDoc}
                              onClear={() => {
                                setFieldValue('lot', '');
                                setFieldValue('plans', '');
                              }}
                              hasClear={!!values.lot}
                              shouldFetch={!!values.neighborhoods}
                              refetch={[values.neighborhoods]}
                              showEmptyOption
                              emptyOptionText="No lots available"
                              apiRequest={() =>
                                getSalesNeighborhoodAvailableLots(values.neighborhoods, false)
                              }
                              onChange={e => {
                                setFieldValue('lot', e.target.value);
                                setFieldValue('plans', '');
                              }}
                              transformResponse={(res: IDropdownResponse[]) => {
                                return res?.map(r => ({
                                  label: `${r.value}`,
                                  value: `${r.value}`
                                }));
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={4} lg={3}>
                            <SalesPlansDropdown
                              isRequired={true}
                              isDisabled={!values.lot || isGeneratingDoc}
                              value={values.plans}
                              handleChange={val => {
                                setFieldValue('plans', val);
                              }}
                              apiParams={{
                                neighborhoodId: values.neighborhoods,
                                lotNumber: Number(values.lot),
                                forMarketingMaterials: true
                              }}
                              isEnabled={!!values.neighborhoods && !!values.lot}
                            />
                          </Grid>
                          <Grid item xs={12} sm={'auto'}>
                            <LoadingButton
                              id="generate-upgrade-pricing"
                              type="button"
                              color="primary"
                              variant="contained"
                              disabled={!values.plans}
                              loading={isGeneratingDoc}
                              startIcon={<Description />}
                              className={classes.generateBtn}
                              onClick={() =>
                                handleDownload(
                                  Number(values.neighborhoods),
                                  Number(values.lot),
                                  Number(values.plans)
                                )
                              }
                            >
                              Generate & Download
                            </LoadingButton>
                          </Grid>
                        </Grid>
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Box>
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  generateBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));
